import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { getUserProfile, updateUserProfile } from '@/modules/common/api/opsium-user.api';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { UpdateOpsiumUserProfileRequest } from '@/modules/common/types/dto/request/update-opsium-user-profile-request';
import {
  invalidateTeamMemberQueries,
  invalidateUserProfileQueries,
} from '@/modules/common/utils/query-invalidation';
import { USER_PROFILE_QUERY_KEY } from '@/modules/settings/hooks/account/use-profile-avatar-config';

/**
 * Hook for managing user profile data and updates
 * Provides query for fetching profile and mutation for updates
 */
export function useUserProfile() {
  const { t } = useTranslation(['authenticated.settings']);
  const { displayToastErrorFromApiWithFallback } = useApiError();
  const queryClient = useQueryClient();

  const {
    data: userProfile,
    isLoading,
    error,
  } = useQuery({
    queryKey: USER_PROFILE_QUERY_KEY,
    queryFn: getUserProfile,
    staleTime: 1000 * 60 * 30, // 30 minutes - user profile rarely changes
    gcTime: 1000 * 60 * 60, // 1 hour
  });

  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateOpsiumUserProfileRequest) => updateUserProfile(data),
    onSuccess: async () => {
      // Refetch rather than setQueryData — the PATCH response may not include all fields (e.g. profileImageUrl)
      await invalidateUserProfileQueries(queryClient);
      // Invalidate team members since the current user appears there too (name change affects list)
      invalidateTeamMemberQueries(queryClient);
      displayToastSuccess(t('authenticated.settings:toasts.profileUpdatedSuccess'));
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.settings:toasts.updateProfileError'
      );
    },
  });

  return {
    userProfile,
    isLoading,
    error,
    updateProfile: updateProfileMutation.mutate,
    isUpdating: updateProfileMutation.isPending,
  };
}
